import { action, flow, flowResult, makeObservable, observable } from "mobx";
import { getTokensPrice } from "../../api/ApiManager";
import { Ticker } from "../../entities/Ticker/TickerTypes";
import {
  IConvertion,
  IDedustCurrency,
  IRaffleCurrency,
} from "./ConvertionTypes";

class ConvertionRatesStore {
  private abortController = new AbortController();

  tonToUsdtConvertionRate: number | null = null;
  raffConvertionRate: number | null = null;
  usdtConvertionRate: number | null = null;

  constructor() {
    makeObservable<ConvertionRatesStore, "fetchRatesFlow">(this, {
      tonToUsdtConvertionRate: observable,
      raffConvertionRate: observable,
      usdtConvertionRate: observable,

      setTonConvertionRate: action,
      setRaffConvertionRate: action,
      setUsdtConvertionRate: action,
      cancel: action,

      fetchRatesFlow: flow,
    });
  }

  setTonConvertionRate(value: number | null) {
    this.tonToUsdtConvertionRate = value;
  }

  setRaffConvertionRate(value: number) {
    this.raffConvertionRate = value;
  }

  setUsdtConvertionRate(value: number) {
    this.usdtConvertionRate = value;
  }

  fetchRates() {
    this.resetRates();
    return flowResult(this.fetchRatesFlow());
  }

  private *fetchRatesFlow() {
    const controller = new AbortController();
    this.abortController = controller;

    try {
      const backendResponse: IConvertion = yield getTokensPrice(
        this.abortController.signal
      );

      // TODO: add a check for object fields
      if (backendResponse) {
        this.setTonConvertionRate(backendResponse.TON.prices.USDT);
        this.setUsdtConvertionRate(backendResponse.USDT.prices.USDT);
        this.setRaffConvertionRate(backendResponse.RAFF.prices.USDT);
        return;
      }

      const dedustResponse: Response = yield fetch(
        "https://api.dedust.io/v2/prices",
        {
          signal: this.abortController.signal,
        }
      );

      if (this.abortController.signal.aborted) return;

      const dedustData: IDedustCurrency[] = yield dedustResponse.json();

      const tonPrice =
        dedustData.find(
          (item: IDedustCurrency) => item.symbol === Ticker.TON
        ) ?? null;
      if (tonPrice?.price) {
        this.setTonConvertionRate(tonPrice.price);
      }

      const usdtPrice =
        dedustData.find(
          (item: IDedustCurrency) => item.symbol === Ticker.USDT
        ) ?? null;
      if (usdtPrice?.price) {
        this.setTonConvertionRate(usdtPrice.price);
      }

      const raffleResponse: Response = yield fetch(
        "https://api.tonraffles.app/api/v1/public/raff/stats",
        {
          signal: this.abortController.signal,
        }
      );

      if (this.abortController.signal.aborted) return;

      const raffleData: IRaffleCurrency = yield raffleResponse.json();
      if (raffleData.price) {
        this.setRaffConvertionRate(raffleData.price);
      }
    } catch (error: unknown) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return;
      }
      throw error;
    } finally {
      if (this.abortController.signal.aborted) {
        this.abortController = new AbortController();
      }
    }
  }

  cancel() {
    if (!this.abortController.signal.aborted) {
      this.abortController.abort();
    }
  }

  private resetRates() {
    this.setTonConvertionRate(null);
    this.usdtConvertionRate = null;
    this.raffConvertionRate = null;
  }
}

export default ConvertionRatesStore;
